// client/src/shared/lib/enroll.ts
// Appel authentifié de l'endpoint d'enrôlement Iris (mint d'un token d'enrôlement).
//
// Route serveur (Fastify 3200, REST — PAS tRPC) :
//   POST /staxx/iris/enroll/mint  (preHandler: requireAuth)
//   req  { machineLabel: string }   (1-64 chars, [A-Za-z0-9 _-])
//   resp { machineId, enrollmentToken, expiresAt }  (200 re-mint | 201 nouveau)
//   err  400 invalid-label · 401 Unauthorized · 5xx internal
//
// Le token d'enrôlement est un secret usage-unique (TTL 60 min). Il n'est JAMAIS
// loggué ni persisté côté client — uniquement affiché à l'écran (sa destination,
// ADR-0002 : livraison hors bundle via staxx:// ou collage).

import { useAuthStore } from '../stores/auth.store'
import { attemptTokenRefresh } from './trpc'

export interface MintResult {
  machineId: string
  /** Token d'enrôlement en clair — affiché à l'écran uniquement, jamais loggué. */
  enrollmentToken: string
  /** ISO 8601 — échéance du token (TTL 60 min). */
  expiresAt: string
}

export type MintErrorKind = 'invalid-label' | 'unauthorized' | 'server' | 'network'

export interface MintError {
  kind: MintErrorKind
  message: string
}

export type MintOutcome =
  | { ok: true; data: MintResult }
  | { ok: false; error: MintError }

const ENROLL_MINT_URL = '/staxx/iris/enroll/mint'

const ERROR_MESSAGES: Record<MintErrorKind, string> = {
  'invalid-label':
    'Nom de machine invalide. Utilisez 1 à 64 caractères : lettres, chiffres, espace, tiret ou underscore.',
  unauthorized:
    'Session expirée ou non autorisée. Reconnectez-vous puis réessayez.',
  server: 'Erreur serveur lors de la génération du token. Réessayez dans un instant.',
  network: 'Connexion impossible au serveur. Vérifiez votre réseau et réessayez.',
}

function fail(kind: MintErrorKind): MintOutcome {
  return { ok: false, error: { kind, message: ERROR_MESSAGES[kind] } }
}

function doRequest(machineLabel: string, token: string): Promise<Response> {
  return fetch(ENROLL_MINT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ machineLabel }),
  })
}

/**
 * Émet un token d'enrôlement pour la machine `machineLabel`.
 * Mappe les codes HTTP en erreurs typées. Sur 401 : tente un refresh
 * (cookie httpOnly) puis rejoue une fois, comme le client tRPC.
 */
export async function mintEnrollment(machineLabel: string): Promise<MintOutcome> {
  const token = useAuthStore.getState().accessToken
  if (!token) return fail('unauthorized')

  let res: Response
  try {
    res = await doRequest(machineLabel, token)

    if (res.status === 401) {
      const refreshed = await attemptTokenRefresh()
      if (!refreshed) {
        useAuthStore.getState().clearAuth()
        return fail('unauthorized')
      }
      const newToken = useAuthStore.getState().accessToken
      if (!newToken) return fail('unauthorized')
      res = await doRequest(machineLabel, newToken)
    }
  } catch {
    return fail('network')
  }

  if (res.ok) {
    try {
      const data = (await res.json()) as MintResult
      if (!data?.enrollmentToken || !data?.machineId || !data?.expiresAt) {
        return fail('server')
      }
      return { ok: true, data }
    } catch {
      return fail('server')
    }
  }

  if (res.status === 400) return fail('invalid-label')
  if (res.status === 401 || res.status === 403) return fail('unauthorized')
  return fail('server')
}

/** Construit le lien d'enrôlement 1-clic consommé par l'app Mac (ADR-0002). */
export function buildStaxxEnrollUrl(enrollmentToken: string): string {
  return `staxx://enroll?token=${encodeURIComponent(enrollmentToken)}`
}
